import { type ReactNode, useCallback, useState } from 'react';

import type { useTheme } from './theme';
import { useTransientTip } from './useTransientTip';

// Burn+ S2 "help mode" (Antenore 2026-07-02) — long-press tooltips are
// invisible, so nobody discovers them. Instead a `?` toggle enters an explain
// mode: while it is ON, tapping a qualifier chip PINS that chip's tip (it
// stays until dismissed) instead of performing the chip's action; toggling the
// `?` off clears the tip and restores normal taps.
//
// It composes the shared transient-tip primitive: one balloon, one Hints gate.
// The card owns a single instance (FocusPanel) and hands `helpMode` + `showTip`
// down to every row that carries explainable chips (FocusPanelChipRow, the
// SetTypeChipBar), so a Dropset tip and an AMRAP tip surface in the same place.
export function useHelpMode(theme: ReturnType<typeof useTheme>): {
  /** Whether explain mode is active — rows read it to route chip taps. */
  helpMode: boolean;
  /** Flip explain mode; leaving it clears any pinned tip. */
  toggleHelpMode: () => void;
  /** Show a chip's explanation. Help mode passes { pinned: true }; a control
   *  tapped to reveal a fact passes { explicit: true } to skip the Hints gate
   *  while still auto-dismissing. */
  showTip: (
    message: string,
    opts?: { pinned?: boolean; explicit?: boolean },
  ) => void;
  /** The shared balloon node — the card renders it once, under the chip row. */
  tipNode: ReactNode;
} {
  const { showTip, clearTip, tipNode } = useTransientTip(theme);
  const [helpMode, setHelpMode] = useState(false);
  const toggleHelpMode = useCallback(() => {
    setHelpMode((on) => {
      if (on) clearTip();
      return !on;
    });
  }, [clearTip]);
  return { helpMode, toggleHelpMode, showTip, tipNode };
}
